/**
 * A connect that a newer one superseded (persona switch, logout, query cancellation). It is a
 * cancellation, not a failure — `reportApiError` drops it like an AbortError. Chat and stories
 * subclass it so stack traces and breadcrumbs still name the connection that was superseded.
 */
export class ConnectSupersededError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConnectSupersededError";
  }
}

/**
 * Whether an error is (or wraps) a superseded connect. The Stream SDKs do not rethrow a token
 * provider's error as-is: stream-chat wraps it in `new Error("Call to tokenProvider failed…",
 * { cause })`, feeds-client retries the provider and then wraps the same way — so a plain
 * `instanceof` never matches the persona-changed throw. Walk the cause chain instead.
 */
const MAX_CAUSE_DEPTH = 10;

export const isConnectSupersededError = (error: unknown): boolean => {
  let current = error;

  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
    if (current instanceof ConnectSupersededError) {
      return true;
    }
    if (!(current instanceof Error)) {
      return false;
    }
    current = current.cause;
  }

  // Bounded so a cyclic cause chain cannot hang the reporter.
  return false;
};
